import * as zod from 'zod';

const BaseWeavingConfig = zod.object({
  enabled: zod.boolean().default(false),
});

// Zigzag pattern with amplitude and period
const ZigzagWeavingConfig = BaseWeavingConfig.extend({
  pattern: zod.literal('zigzag'),
  amplitude: zod.number().positive(), // meters
  period: zod.number().positive(), // meters
  dwellLeft: zod.number().nonnegative(), // ms
  dwellRight: zod.number().nonnegative(), // ms
});

// Whip stitch pattern with reverse and forward offsets
const WhipWeavingConfig = BaseWeavingConfig.extend({
  pattern: zod.literal('whip'),
  reverseOffset: zod.number().gte(0.0001), // meters, min 0.1mm
  forwardOffset: zod.number().gte(0.0001), // meters, min 0.1mm
  dwellForward: zod.number().nonnegative(), // ms
  dwellReverse: zod.number().nonnegative(), // ms
});

export const WeavingConfig = zod.discriminatedUnion('pattern', [
  ZigzagWeavingConfig,
  WhipWeavingConfig,
]);

export type WeavingConfig = zod.infer<typeof WeavingConfig>;

export type ZigzagWeavingConfig = zod.infer<typeof ZigzagWeavingConfig>;
export type WhipWeavingConfig = zod.infer<typeof WhipWeavingConfig>;

export const ZigzagDefaults = {
  enabled: false,
  pattern: 'zigzag' as const,
  period: 0.005,
  amplitude: 0.003,
  dwellLeft: 100,
  dwellRight: 100,
};

export const WhipDefaults = {
  enabled: false,
  pattern: 'whip' as const,
  reverseOffset: 0.001, // 1mm
  forwardOffset: 0.006,
  dwellForward: 100,
  dwellReverse: 100,
};

export const getWeavingDefaults = (pattern: 'zigzag' | 'whip') => {
  switch (pattern) {
    case 'zigzag':
      return ZigzagDefaults;
    case 'whip':
      return WhipDefaults;
    default:
      throw new Error(`Unknown weaving pattern: ${pattern}`);
  }
};

export const defaultWeavingConfig = () => ZigzagDefaults;

export const isZigzagPattern = (
  config: WeavingConfig,
): config is ZigzagWeavingConfig => {
  return config.pattern === 'zigzag';
};

export const isWhipPattern = (
  config: WeavingConfig,
): config is WhipWeavingConfig => {
  return config.pattern === 'whip';
};

export const getWeavePatternName = (config: WeavingConfig) => {
  switch (config.pattern) {
    case 'zigzag':
      return 'Zigzag';
    case 'whip':
      return 'Whip Stitch';
    default:
      return 'Unknown';
  }
};
